import { useEffect, useState } from 'react'

import { useInterval } from '../../../hooks/useInterval'
import { useNotificationsStore } from '../stores/notificationsStore'

const ACTIVE_MS = 30_000
// A backgrounded tab has nobody watching the badge. Backing off keeps an idle
// tab left open all day from costing ~2,900 requests.
const HIDDEN_MS = 5 * 60_000

/**
 * Polls the cheap /notifications/summary endpoint and pulls the full list only
 * when something actually changed.
 *
 * Mount this ONCE, high in the tree — not per component. Several copies means
 * several pollers hitting the API in lockstep, and mounting it inside the
 * dashboard would stop the bell updating anywhere else in the app.
 *
 * `enabled` is the 'notifications' feature flag. It has to be an argument
 * rather than a caller-side `if`, because hooks can't be called conditionally —
 * and without it every user on an account without the flag would poll a 403 on
 * a loop, twice a minute, forever.
 */
export const useNotificationPolling = (enabled = true) => {
    const fetchSummary = useNotificationsStore((s) => s.fetchSummary)
    const fetchList = useNotificationsStore((s) => s.fetchList)
    const latestAt = useNotificationsStore((s) => s.summary.latest_at)

    const [delay, setDelay] = useState(ACTIVE_MS)

    useEffect(() => {
        if (enabled) fetchSummary()
    }, [enabled, fetchSummary])

    useEffect(() => {
        if (!enabled) return undefined

        const onVisibilityChange = () => {
            const hidden = document.visibilityState === 'hidden'
            setDelay(hidden ? HIDDEN_MS : ACTIVE_MS)
            // Refresh on the way back so the badge is never stale on focus —
            // otherwise the user stares at an old count for up to 30 seconds.
            if (!hidden) fetchSummary()
        }

        document.addEventListener('visibilitychange', onVisibilityChange)
        return () => document.removeEventListener('visibilitychange', onVisibilityChange)
    }, [enabled, fetchSummary])

    // useInterval only schedules for a numeric delay, so null is a clean stop.
    useInterval(fetchSummary, enabled ? delay : null)

    // `latest_at` is the change signal. Refetching the feed on every poll would
    // defeat the point of having a summary endpoint at all — and, because the
    // list endpoint is what marks rows DELIVERED, it would also record delivery
    // for notifications nobody is looking at.
    useEffect(() => {
        if (enabled && latestAt) fetchList({ reset: true })
    }, [enabled, latestAt, fetchList])
}
